import React from "react";
import Balancer from "react-wrap-balancer";
import { Box, Flex, type LayoutProps, type SpaceProps } from "@chakra-ui/react";
import { BookDemoButton } from "components/event-tracking";
import { HeroCTA } from "components/hero/cta";
import { Section } from "components/section";
import { type BrandColor, type ThemeBgColor } from "contexts/color-theme";
import { TextTitle, type TextTitleProps } from "ui/text";
import { FadeIn } from "ui/animations";
import { DecorativeBackgroundImage } from "components/images";
import { getBrandColorHex } from "theme/colors";
import circleAuraBg from "../../../public/assets/heros/aura-stack-bg.png";
import squareAuraBg from "../../../public/assets/heros/aura-square-bg.png";
import { HeroSpotlightBackgroundEffects } from "./spotlight-background-effects";

export type AuraHalfHeroProps = {
  /**
   * Single color variant to use for the background effects.
   * */
  variant: BrandColor;

  /**
   * The shape of the aura.
   * @default "circle"
   */
  auraType?: "circle" | "square";

  /**
   * Optional "eyebrow" text that will be displayed as a chip above the title.
   */
  eyebrow?: string;

  /**
   * Main `h1` title.
   */
  title: string;

  /**
   * Description displayed with the `h1` title.
   */
  description: string;

  /**
   * Maximum width to set on the title and description.
   */
  titleMaxW?: TextTitleProps["maxW"];

  /**
   * By default, only the demo modal button will be shown. Use this to include a signup button, as well.
   */
  includeSignupAction?: boolean; // eslint-disable-line react/boolean-prop-naming

  /**
   * Layout and space props that can optionally be added to the wrapper around the "media" portion of the hero.
   */
  mediaWrapperProps?: LayoutProps & SpaceProps;

  /**
   * The default media to display.
   */
  media: JSX.Element;

  /**
   * The optional alternate media to display in the vertical layout on `md` and smaller screens.
   */
  mobileMedia?: JSX.Element;
};

/**
 * A half-text, half-media hero with a light gradient background and aura effect behind the media.
 */
export function AuraHalfHero({
  variant,
  eyebrow,
  title,
  description,
  titleMaxW,
  includeSignupAction,
  mediaWrapperProps,
  media,
  mobileMedia,
  auraType = "circle",
}: AuraHalfHeroProps) {
  const bgColor: ThemeBgColor = `new.${variant}.150`;
  const bgHex = getBrandColorHex(variant, 150);

  const auraBg = auraType === "circle" ? circleAuraBg : squareAuraBg;

  return (
    <Box as="section" pos="relative" overflow="hidden" bg={bgColor} pt={{ base: 0, lg: "small" }}>
      <HeroSpotlightBackgroundEffects hasBgForHalfHero theme="light" bgColor={bgColor} spotlightColor="white" />

      <Section as="div" themeBgColor={bgColor} my="0">
        <FadeIn duration={0.3}>
          <Flex
            pos="relative"
            direction={{ base: "column", lg: "row" }}
            alignItems="center"
            gap={{ base: "small", lg: "biggest" }}
          >
            <TextTitle
              size="xl"
              chip={eyebrow ? { text: eyebrow } : undefined}
              titleAs="h1"
              title={<Balancer>{title}</Balancer>}
              description={description}
              descriptionAs="h2"
              maxW={titleMaxW}
              actions={includeSignupAction ? <HeroCTA /> : <BookDemoButton />}
              pt={{ base: "bigger", sm: "biggest", lg: "huge" }}
              pb={{ base: 0, lg: "huge" }}
              zIndex={1}
            />

            <Flex pos="relative" alignItems="center" justifyContent="center" w="100%" {...mediaWrapperProps}>
              <Flex
                pos="absolute"
                right={
                  auraType === "circle" ? undefined : { base: "-32%", sm: "-20%", md: "0", lg: "-44%", xl: "-33%" }
                }
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  pos="relative"
                  filter={{ base: `drop-shadow(0 0 60px ${bgHex})`, lg: "none" }}
                  boxSize={{ base: auraType === "circle" ? "525px" : "400px", sm: "565px", md: "600px", lg: "650px" }}
                >
                  <DecorativeBackgroundImage priority src={auraBg} />
                </Box>
              </Flex>

              <Flex pos="relative" boxSize="100%" alignItems="center" justifyContent="center">
                <Box boxSize="100%" display={mobileMedia ? { base: "none", lg: "block" } : "block"}>
                  {media}
                </Box>

                {mobileMedia && (
                  <Flex boxSize="100%" justifyContent="center" display={{ base: "flex", lg: "none" }}>
                    {mobileMedia}
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </FadeIn>
      </Section>

      <Section as="div" my="0">
        <Box h={{ base: "0px", lg: "huge" }} />
      </Section>

      <Box
        pos="absolute"
        display={{ base: "block", lg: "none" }}
        bg="white"
        left="-40px"
        right="-40px"
        bottom="-20px"
        h="60px"
        filter="blur(20px)"
        zIndex={1}
      />
    </Box>
  );
}
